import {
  OfficeButton,
  Paginator,
  TableComponent,
  TableLoader,
} from "components";
import { useNavigate, useSearch } from "react-location";
import { useUrlState, withPermissions } from "utils";

import ActionButton, { Action } from "components/buttons/action-button";
import { FilePlus } from "lucide-react";
import { LocationGenerics } from "router/location";
import { usePagination } from "hooks";
import { useReinsurerKavaPremiumLimits } from "hooks/data/use-re-insurers";
import CreateLimitContainer from "./create";
import RemoveLimitContainer from "./remove";
import UpdateLimitContainer from "./update";
import moment from "moment";

type Props = {
  premiumMemberId: string;
};

const KavaPremiumLimits = ({ premiumMemberId }: Props) => {
  const searchParams = useSearch<LocationGenerics>();
  const navigate = useNavigate<LocationGenerics>();
  const [modal, setModal] = useUrlState("modal");
  const { limit, offset, setPage } = usePagination();
  const { loading, limits, refetch, count } = useReinsurerKavaPremiumLimits({
    premiumMemberId: premiumMemberId,
    limit: limit,
    offset: offset,
  });

  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
    () => {
      navigate({
        search: (old) => ({
          ...old,
          id,
          modal: action,
        }),
      });
    };

  return (
    <div className="pt-6">
      <TableComponent
        hasSearch
        title={"Limits"}
        columns={[
          {
            accessor: "created_by",
            name: "Created by",
            render: (row) => (
              <div className="flex flex-col">
                {row.created_by?.employee_first_name}{" "}
                {row.created_by?.employee_last_name}
              </div>
            ),
          },
          {
            accessor: "updated_by",
            name: "Updated by",
            render: (row) => (
              <div className="flex flex-col">
                {row.updated_by?.employee_first_name}{" "}
                {row.updated_by?.employee_last_name}
              </div>
            ),
          },
          {
            accessor: "participation_lower_limit",
            name: "Limit (%)",
          },
          {
            accessor: "created_at",
            name: "Created at",
            render: (row) => moment(row.created_at).format("DD-MM-YYYY"),
          },

          {
            accessor: "position",
            name: "Actions",
            render: (row) => (
              <div className="flex space-x-2">
                {withPermissions(["*:*", "reinsurers:*", "reinsurers:manage"])(
                  <ActionButton
                    action="update"
                    onClick={dispatchAction(
                      row?.premium_member_limit_id || "",
                      "update"
                    )}
                  />
                )}
                {withPermissions(["*:*", "reinsurers:*", "reinsurers:manage"])(
                  <ActionButton
                    action="remove"
                    onClick={dispatchAction(
                      row?.premium_member_limit_id || "",
                      "remove"
                    )}
                  />
                )}
              </div>
            ),
          },
        ]}
        renderLoader={() => <TableLoader items={[1, 1, 1, 2, 1]} />}
        pagination={() => (
          <Paginator
            offset={offset}
            limit={limit}
            totalItems={count}
            currentSize={limits.length}
            setOffset={setPage}
          />
        )}
        data={limits}
        loading={loading}
        refetch={refetch}
        renderActions={() => (
          <>
            {" "}
            {withPermissions([
              "*:*",
              "reinsurers:*",
              "reinsurers:manage",
              "reinsurers:create",
            ])(
              <OfficeButton onClick={dispatchAction("", "create")}>
                <span className="mr-2 text-sm">Add Limit</span>
                <FilePlus className="h-4 w-5" />
              </OfficeButton>
            )}{" "}
          </>
        )}
      />
      <CreateLimitContainer
        open={modal === "create"}
        setOpen={(val: boolean) => setModal(val ? "create" : undefined)}
        premiumMemberId={premiumMemberId}
        refetch={refetch}
      />

      {searchParams?.id && (
        <>
          <RemoveLimitContainer
            open={modal === "remove"}
            premiumMemberId={premiumMemberId}
            setOpen={(val: boolean) => setModal(val ? "remove" : undefined)}
            refetch={refetch}
          />
          <UpdateLimitContainer
            open={modal === "update"}
            premiumMemberId={premiumMemberId}
            setOpen={(val: boolean) => setModal(val ? "update" : undefined)}
            refetch={refetch}
          />
        </>
      )}
    </div>
  );
};

export default KavaPremiumLimits;
